import type { RenderTarget as ThreeRenderTarget } from "three/webgpu";
import type { GpuContext } from "./GpuContext.js";

/**
 * Reading a rendered frame back out of the GPU, as pixels or as a PNG.
 *
 * **What this is NOT for: rescuing a browser screenshot.** A page-level screenshot of a Webwin canvas
 * works — measured, Chrome + Playwright against a WebGPU canvas, 1280x800: identical to the readback
 * below to within a maximum channel delta of 1 and a mean of 0.02, which is the downscale rounding and
 * nothing else. An earlier version of this comment claimed the compositor drops WebGPU canvases; it
 * does not, and the frames that led to that belief were an overlay and a night sky.
 *
 * **What it IS for — three things a page screenshot cannot do:**
 *
 *   * **Pin a specific frame.** A screenshot catches whatever the compositor had when it was asked; a
 *     capture renders and reads in one task, so the image is of a frame you chose. That is the
 *     difference between a golden-image test that is stable and one that is flaky.
 *   * **Run without a page.** An embedder — Electron, a Node harness, an in-app "save a screenshot"
 *     button, an offscreen canvas in a worker — has a GPU and no screenshot facility at all.
 *   * **Hand back raw pixels.** {@link readFrame} is RGBA8 with no encoder in the way, which is what a
 *     comparison wants; decoding a PNG to diff it is work done twice.
 *
 * **How:** a frame is rendered into a target (`gpu.captureTarget()`) rather than read off the swapchain, so a
 * capture works on platforms that will not hand out a copyable swapchain and is not tied to the task the frame
 * ran in. `Desktop.capture` renders and reads for you and is the call to reach for; these are exported for
 * callers that have rendered into a target of their own.
 */

/** A frame read back from the GPU: RGBA8, top row first, `width * height * 4` bytes. */
export interface CapturedFrame {
  width: number;
  height: number;
  data: Uint8Array;
}

export interface CaptureOptions {
  /**
   * Longest edge of the returned image, in pixels. Larger frames are scaled down.
   *
   * A full-resolution capture of a 4K canvas is a 33 MB buffer and a PNG encode to match; most callers
   * are looking at a picture, not measuring it. Omit for no scaling.
   */
  maxWidth?: number;
}

/**
 * The last rendered frame as an image, scaled to `maxWidth` if asked.
 *
 * Separated from the PNG encode because the two have different costs and different callers: a
 * golden-image test wants the raw buffer and no encoder, and a person wants a PNG.
 */
export async function readFrame(gpu: GpuContext, target: ThreeRenderTarget, options: CaptureOptions = {}): Promise<CapturedFrame> {
  const frame = await gpu.readTarget(target);
  const max = options.maxWidth;
  if (max === undefined || frame.width <= max) return frame;

  const scale = max / frame.width;
  const w = Math.max(1, Math.round(frame.width * scale));
  const h = Math.max(1, Math.round(frame.height * scale));
  const canvas = toCanvas(frame);
  const out = new OffscreenCanvas(w, h);
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("webwin: 2D context unavailable for capture scaling.");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(canvas, 0, 0, w, h);
  const scaled = ctx.getImageData(0, 0, w, h);
  return { width: w, height: h, data: new Uint8Array(scaled.data.buffer.slice(0)) };
}

/** The last rendered frame as a PNG. */
export async function readFramePng(gpu: GpuContext, target: ThreeRenderTarget, options: CaptureOptions = {}): Promise<Blob> {
  const frame = await readFrame(gpu, target, options);
  return toCanvas(frame).convertToBlob({ type: "image/png" });
}

/**
 * The last rendered frame as a `data:image/png;base64,…` URL.
 *
 * The form an automation tool can actually carry: `page.evaluate` marshals a string, and neither a
 * `Blob` nor a `Uint8Array` survives the trip out of the page intact.
 */
export async function readFrameDataUrl(gpu: GpuContext, target: ThreeRenderTarget, options: CaptureOptions = {}): Promise<string> {
  const blob = await readFramePng(gpu, target, options);
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  // Chunked: `String.fromCharCode(...bytes)` on a multi-megabyte image overflows the argument limit,
  // which presents as a RangeError from a line that looks like it is only formatting.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return `data:image/png;base64,${btoa(binary)}`;
}

function toCanvas(frame: CapturedFrame): OffscreenCanvas {
  const canvas = new OffscreenCanvas(frame.width, frame.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("webwin: 2D context unavailable for capture encoding.");
  // Copied rather than wrapped: `ImageData` insists on a plain ArrayBuffer, and a view over the
  // readback's buffer is typed as possibly shared.
  const pixels = new Uint8ClampedArray(frame.width * frame.height * 4);
  pixels.set(frame.data);
  ctx.putImageData(new ImageData(pixels, frame.width, frame.height), 0, 0);
  return canvas;
}
